import React, { useState } from 'react';

/** Walkmore canvas — keep product white; layout matches BetterMe-style shells. */
export const V3_CANVAS = '#FFFFFF';

export const V3_CTA_CLASS =
    'w-full min-h-[56px] font-poppins font-bold text-[18px] leading-[1.35] text-white py-[17px] rounded-[18px] bg-navy transition-transform duration-100 ease-out';

interface FunnelV3CtaButtonProps {
    label: React.ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    type?: 'button' | 'submit';
    className?: string;
    children?: React.ReactNode;
}

/** Shared primary CTA with light press feedback (same feel as Sw/Mf). */
export const FunnelV3CtaButton: React.FC<FunnelV3CtaButtonProps> = ({
    label,
    onClick,
    disabled = false,
    type = 'button',
    className = '',
    children,
}) => {
    const [pressed, setPressed] = useState(false);

    return (
        <button
            type={type}
            onClick={onClick}
            disabled={disabled}
            onPointerDown={() => {
                if (!disabled) setPressed(true);
            }}
            onPointerUp={() => setPressed(false)}
            onPointerLeave={() => setPressed(false)}
            onPointerCancel={() => setPressed(false)}
            className={`${V3_CTA_CLASS} ${
                disabled ? 'opacity-40 cursor-not-allowed' : 'active:opacity-90'
            } ${className}`}
            style={{
                transform: pressed && !disabled ? 'scale(0.97)' : 'scale(1)',
            }}
        >
            {children ?? label}
        </button>
    );
};

interface FunnelV3ScreenProps {
    children: React.ReactNode;
    cta?: React.ReactNode;
    ctaDisabled?: boolean;
    onCta?: () => void;
    ctaLabel?: string;
    /** Tap-to-advance screens omit the pinned footer. */
    hideCta?: boolean;
    /** Extra classes on the outer shell (e.g. dark interstitial backgrounds). */
    className?: string;
    /** Override scroll-region padding (default pt-6). */
    contentClassName?: string;
    canvas?: string;
}

/**
 * BetterMe-style quiz shell for walkmore Funnel V3.
 * Content scrolls; CTA stays pinned in-flow with safe-area — never covers options.
 */
export const FunnelV3Screen: React.FC<FunnelV3ScreenProps> = ({
    children,
    cta,
    ctaDisabled,
    onCta,
    ctaLabel = 'Continue',
    hideCta = false,
    className = '',
    contentClassName = '',
    canvas = V3_CANVAS,
}) => {
    return (
        <div
            className={`flex-1 flex flex-col min-h-0 ${className}`}
            style={{ backgroundColor: canvas }}
        >
            <div
                className={`flex-1 overflow-y-auto max-w-md mx-auto w-full ${
                    contentClassName.includes('px-') || contentClassName.includes('!px') ? '' : 'px-5'
                } ${
                    contentClassName.includes('pt-') || contentClassName.includes('!pt') ? '' : 'pt-6'
                } ${hideCta ? 'pb-8' : 'pb-3'} ${contentClassName}`}
            >
                {children}
            </div>
            {!hideCta && (
                <div
                    className="shrink-0 px-5 pt-3"
                    style={{
                        backgroundColor: canvas,
                        paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))',
                    }}
                >
                    <div className="max-w-md mx-auto">
                        {cta || (
                            <FunnelV3CtaButton
                                label={ctaLabel}
                                onClick={onCta}
                                disabled={ctaDisabled || !onCta}
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FunnelV3Screen;
